import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  AccessTime,
  CalendarToday,
  Close,
  CropFree,
  Edit,
  PersonOutline,
  Place,
  Save,
  VpnKey,
} from '@material-ui/icons';
import DateFnsUtils from '@date-io/date-fns';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
  KeyboardTimePicker,
} from '@material-ui/pickers';
import { format, isValid, parse } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { useStyles } from './styles';


const generateAccessCode = () =>
  Array.from({ length: 6 }, () => Math.floor(Math.random() * 10)).join('');

const getDateValue = (value) => {
  if (!value) return null;

  const parsedWithDate = parse(value, 'dd/MM/yyyy', new Date());
  if (isValid(parsedWithDate)) {
    return parsedWithDate;
  }

  const parsedWithIso = parse(value, 'yyyy-MM-dd', new Date());
  if (isValid(parsedWithIso)) {
    return parsedWithIso;
  }

  const nativeDate = new Date(value);
  return isValid(nativeDate) ? nativeDate : null;
};

const getTimeValue = (value) => {
  if (!value) return null;

  const normalizedValue = value.includes('-')
    ? value.split('-')[0].trim()
    : value;
  const parsedValue = parse(normalizedValue, 'HH:mm', new Date());
  return isValid(parsedValue) ? parsedValue : null;
};

const parseLessonContent = (lessonContent) => {
  if (!lessonContent) {
    return {
      title: '',
      instructor: '',
      date: '',
      time: '',
      location: '',
    };
  }

  const content = lessonContent.content;

  if (content && typeof content === 'object') {
    return {
      title: lessonContent.title || content.title || '',
      instructor:
        lessonContent.instructor || content.instructor || content.teacher || '',
      date: lessonContent.date || content.date || '',
      time: lessonContent.time || content.time || '',
      location: lessonContent.location || content.location || '',
    };
  }

  if (typeof content === 'string' && content.trim()) {
    try {
      const parsedContent = JSON.parse(content);
      return {
        title: lessonContent.title || parsedContent.title || '',
        instructor:
          lessonContent.instructor ||
          parsedContent.instructor ||
          parsedContent.teacher ||
          '',
        date: lessonContent.date || parsedContent.date || '',
        time: lessonContent.time || parsedContent.time || '',
        location: lessonContent.location || parsedContent.location || '',
      };
    } catch (error) {
      return {
        title: lessonContent.title || '',
        instructor: lessonContent.instructor || '',
        date: lessonContent.date || '',
        time: lessonContent.time || '',
        location: lessonContent.location || '',
        lessonCode: lessonContent.lesson_code || '',
      };
    }
  }

  return {
    title: lessonContent.title || '',
    instructor: lessonContent.instructor || '',
    date: lessonContent.date || '',
    time: lessonContent.time || '',
    location: lessonContent.location || '',
    lessonCode: lessonContent.lesson_code || '',
  };
};

export default function PresentialLessonModal({
  open,
  onClose,
  lesson,
  lessonContent,
  onSave,
  courseId,
  titleName = '',
  teacherName = '',
  lessonDate = '',
  lessonTime = '',
  location = '',
}) {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState('qrcode');
  const [accessCode, setAccessCode] = useState(generateAccessCode);
  const [isEditing, setIsEditing] = useState(false);
  const [formValues, setFormValues] = useState({
    title: titleName,
    instructor: teacherName,
    date: lessonDate,
    time: lessonTime,
    location,
  });

  useEffect(() => {
    if (!open) return;

    setActiveTab('qrcode');
    // setAccessCode(generateAccessCode());

    const parsedValues = parseLessonContent(lessonContent) || {};
    const hasContent =
      parsedValues.title ||
      parsedValues.instructor ||
      parsedValues.date ||
      parsedValues.time ||
      parsedValues.location


    console.log('Parsed Lesson Content:', lessonContent?.lesson_code);

    const initialLessonCode = lessonContent?.lesson_code || generateAccessCode();

    setFormValues({
      title: parsedValues.title || titleName,
      instructor: parsedValues.instructor || teacherName,
      date: parsedValues.date || lessonDate,
      time: parsedValues.time || lessonTime,
      location: parsedValues.location || location,
    });
    setAccessCode(initialLessonCode);
    setIsEditing(!hasContent);
  }, [open, lessonContent, teacherName, lessonDate, lessonTime, location]);

  const handleGenerateNewCode = () => {
    setAccessCode(generateAccessCode());
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));
  };

  const handleDateChange = (date) => {
    setFormValues((currentValues) => ({
      ...currentValues,
      date:
        date && isValid(date)
          ? format(date, 'dd/MM/yyyy')
          : '',
    }));
  };

  const handleTimeChange = (time) => {
    setFormValues((currentValues) => ({
      ...currentValues,
      time:
        time && isValid(time)
          ? format(time, 'HH:mm')
          : '',
    }));
  };

  const handleEnableEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    const parsedValues = parseLessonContent(lessonContent) || {};
    setFormValues({
      title: parsedValues.title || titleName,
      instructor: parsedValues.instructor || teacherName,
      date: parsedValues.date || lessonDate,
      time: parsedValues.time || lessonTime,
      location: parsedValues.location || location,
    });
    setIsEditing(false);
  };

  const handleSave = () => {
    if (onSave) {
      onSave({
        ...formValues,
        lessonCode: accessCode,
      });
    }
    setIsEditing(false);
  };

  const qrValue = useMemo(() => {
    const lessonPath = `${window.location.origin}/course/${courseId}/learn/${lesson?.id_lesson}?code=${accessCode}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
      lessonPath
    )}`;
  }, [courseId, lesson, accessCode]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent className={classes.paper}>
        <div className={classes.header}>
          <div>
            <Typography className={classes.title}>Aula Presencial</Typography>
            <Typography className={classes.subtitle}>
              Valide sua presença escaneando o QR Code ou usando o código de 6
              dígitos.
            </Typography>
          </div>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </div>

        <div className={classes.infoCard}>
          <div className={classes.infoHeader}>
            <Typography className={classes.lessonName}>
              {''}
            </Typography>
            <IconButton
              size="small"
              className={classes.editButton}
              onClick={isEditing ? handleCancelEdit : handleEnableEdit}
            >
              {isEditing ? <Close fontSize="small" /> : <Edit fontSize="small" />}
            </IconButton>
          </div>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Nome da Aula"
                  name="title"
                  value={formValues.title}
                  onChange={handleFieldChange}
                  disabled={!isEditing}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Instrutor"
                  name="instructor"
                  value={formValues.instructor}
                  onChange={handleFieldChange}
                  disabled={!isEditing}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutline className={classes.fieldIcon} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <KeyboardDatePicker
                  fullWidth
                  autoOk
                  inputVariant="outlined"
                  variant="inline"
                  label="Data"
                  format="dd/MM/yyyy"
                  value={getDateValue(formValues.date)}
                  onChange={handleDateChange}
                  disabled={!isEditing}
                  InputAdornmentProps={{ position: 'start' }}
                  keyboardIcon={<CalendarToday className={classes.fieldIcon} />}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <KeyboardTimePicker
                  fullWidth
                  autoOk
                  inputVariant="outlined"
                  variant="inline"
                  ampm={false}
                  label="Hora"
                  value={getTimeValue(formValues.time)}
                  onChange={handleTimeChange}
                  disabled={!isEditing}
                  keyboardIcon={<AccessTime className={classes.fieldIcon} />}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Local"
                  name="location"
                  value={formValues.location}
                  onChange={handleFieldChange}
                  disabled={!isEditing}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Place className={classes.fieldIcon} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </MuiPickersUtilsProvider>
        </div>

        <div className={classes.switch}>
          <button
            type="button"
            className={`${classes.switchBtn} ${activeTab === 'qrcode' ? classes.activeSwitchBtn : ''
              }`}
            onClick={() => setActiveTab('qrcode')}
          >
            <CropFree fontSize="small" /> QR Code
          </button>
          <button
            type="button"
            className={`${classes.switchBtn} ${activeTab === 'code' ? classes.activeSwitchBtn : ''
              }`}
            onClick={() => setActiveTab('code')}
          >
            <VpnKey fontSize="small" /> Código
          </button>
        </div>

        {activeTab === 'qrcode' ? (
          <>
            <div className={classes.qrBox}>
              <img className={classes.qrImage} src={qrValue} alt="QR Code" />
            </div>
            <Typography className={classes.qrHelp}>
              Aponte a câmera para o QR Code para validar a presença.
            </Typography>
          </>
        ) : (
          <>
            <div className={classes.codeWrap}>
              {accessCode
                ?.toString()
                .split('').map((digit, index) => (
                  <div className={classes.digit} key={`digit-${index}`}>
                    {digit}
                  </div>
                ))}
            </div>
            <Typography className={classes.codeHelp}>
              Informe este código para registrar sua presença e conclusão da aula.
            </Typography>
            <Typography className={classes.expires}>
              Código atual: {accessCode}
            </Typography>
          </>
        )}
        <DialogActions className={classes.footer}>
          {activeTab === 'code' && (
            <Button className={classes.closeBtn} onClick={handleGenerateNewCode}>
              Gerar novo código
            </Button>
          )}
          {isEditing ? (
            <>
              <Button className={classes.closeBtn} onClick={handleCancelEdit}>
                Cancelar
              </Button>
              <Button
                color="primary"
                variant="contained"
                size="large"
                className={classes.generateBtn}
                onClick={handleSave}
              >
                <Save style={{ marginRight: 8 }} />
                Salvar
              </Button>
            </>
          ) : (
            <>
              <Button className={classes.closeBtn} onClick={onClose}>
                Fechar
              </Button>
            </>
          )}
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}
